// src/pages/AdminWorkspaces.js

import React, { useState, useEffect } from 'react'
import { db } from '../firebase'
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  query,
  where,
  arrayUnion,
  arrayRemove,
  getDoc,
  getDocs,
  writeBatch
} from 'firebase/firestore'
import { useAuth } from '../contexts/AuthContext'
import {
  Container,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Divider,
  Box,
  Alert,
  Snackbar,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Autocomplete,
  Avatar,
  ListItemAvatar,
  Tooltip,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import ChannelIcon from '@mui/icons-material/Chat'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import SearchIcon from '@mui/icons-material/Search'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import TagIcon from '@mui/icons-material/LocalOffer'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function AdminWorkspaces() {
  const { currentUser, userRole } = useAuth()
  const { t } = useTranslation()

  // Admin kontrolü
  const [isAdmin, setIsAdmin] = useState(false)

  // Workspaces
  const [workspaceName, setWorkspaceName] = useState('')
  const [workspaces, setWorkspaces] = useState([])
  const [selectedWorkspace, setSelectedWorkspace] = useState(null)

  // Channels
  const [channelName, setChannelName] = useState('')
  const [channels, setChannels] = useState([])
  const [selectedChannel, setSelectedChannel] = useState(null)

  // Checkbox: Kullanıcı cevap sayfasında bu kanala link gösterilsin mi
  const [showChannelLink, setShowChannelLink] = useState(false)

  // Users
  const [users, setUsers] = useState([])
  const [searchUser, setSearchUser] = useState('')
  const [filteredUsers, setFilteredUsers] = useState([])

  // Tags
  const [newTag, setNewTag] = useState('')
  const [tags, setTags] = useState([])

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })

  // Dialog state for editing
  const [editDialog, setEditDialog] = useState({
    open: false,
    type: '', // 'workspace' or 'channel' or 'tag'
    item: null,
    newName: '',
    newShowChannelLink: false
  })

  // Dialog state for deletion confirmation
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: '',
    item: null
  })

  // Dialog state for adding member
  const [addMemberDialog, setAddMemberDialog] = useState({
    open: false,
    user: null
  })

  useEffect(() => {
    if (userRole === 'admin') {
      setIsAdmin(true)
    } else {
      setIsAdmin(false)
    }

    const workspacesRef = collection(db, 'workspaces')
    const unsubscribeWorkspaces = onSnapshot(workspacesRef, (snapshot) => {
      const wsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      setWorkspaces(wsData)
    })

    const usersRef = collection(db, 'users')
    const unsubscribeUsers = onSnapshot(usersRef, (snapshot) => {
      const userData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      setUsers(userData)
    })

    return () => {
      unsubscribeWorkspaces()
      unsubscribeUsers()
    }
  }, [currentUser, userRole])

  useEffect(() => {
    if (selectedChannel) {
      // Filtrelenmiş kullanıcıları güncelle: Kanala eklenmemiş olanlar
      const availableUsers = users
        .filter((user) => user.status !== 'passive')
        .filter((user) => user.emailVerified === true)
        .filter((user) => !selectedChannel.members?.includes(user.id))

      const searchedUsers = availableUsers.filter((user) =>
        user.email.toLowerCase().includes(searchUser.toLowerCase())
      )
      setFilteredUsers(searchedUsers)
    } else {
      setFilteredUsers([])
    }
  }, [searchUser, users, selectedChannel])

  useEffect(() => {
    if (selectedWorkspace) {
      setTags(selectedWorkspace.tags || [])
    } else {
      setTags([])
    }
  }, [selectedWorkspace])

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  // Workspace oluştur
  const handleCreateWorkspace = async () => {
    if (!workspaceName.trim()) {
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.workspaceNameEmptyWarning'),
        severity: 'warning'
      })
      return
    }
    try {
      await addDoc(collection(db, 'workspaces'), {
        name: workspaceName,
        description: '',
        createdAt: serverTimestamp(),
        tags: []
      })
      setWorkspaceName('')
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.workspaceCreateSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Workspace oluşturulurken hata:', error)
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.workspaceCreateError'),
        severity: 'error'
      })
    }
  }

  // Workspace seç
  const handleSelectWorkspace = (workspace) => {
    setSelectedWorkspace(workspace)
    setSelectedChannel(null)

    if (!workspace?.id) return
    const channelRef = collection(db, 'channels')
    const qChannel = query(channelRef, where('workspaceId', '==', workspace.id))
    const unsubscribeChannels = onSnapshot(qChannel, (snapshot) => {
      const channelData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      setChannels(channelData)
    })

    return () => {
      unsubscribeChannels && unsubscribeChannels()
    }
  }

  // Yeni kanal oluştur
  const handleCreateChannel = async () => {
    if (!channelName.trim() || !selectedWorkspace) {
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.channelNameEmptyWarning'),
        severity: 'warning'
      })
      return
    }
    try {
      await addDoc(collection(db, 'channels'), {
        workspaceId: selectedWorkspace.id,
        name: channelName,
        description: '',
        createdAt: serverTimestamp(),
        members: [],
        memberDetails: [],
        showChannelLink
      })
      setChannelName('')
      setShowChannelLink(false)
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.channelCreateSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Kanal oluşturulurken hata:', error)
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.channelCreateError'),
        severity: 'error'
      })
    }
  }

  // Kanal seç
  const handleSelectChannel = (channel) => {
    setSelectedChannel(channel)
    setSearchUser('')
  }

  // Kanala üye ekle
  const handleAddMemberToChannel = async (userId) => {
    if (!selectedChannel || !userId) {
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.userSelectionRequired'),
        severity: 'warning'
      })
      return
    }

    try {
      const channelRef = doc(db, 'channels', selectedChannel.id)
      await updateDoc(channelRef, {
        members: arrayUnion(userId),
        memberDetails: arrayUnion({
          userId,
          role: 'participant',
          tags: []
        })
      })

      setSelectedChannel({
        ...selectedChannel,
        members: [...(selectedChannel.members || []), userId],
        memberDetails: [
          ...(selectedChannel.memberDetails || []),
          {
            userId,
            role: 'participant',
            tags: []
          }
        ]
      })
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.userAddSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Kullanıcı eklenirken hata:', error)
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.userAddError'),
        severity: 'error'
      })
    }
  }

  // Kanaldan üye çıkar
  const handleRemoveMemberFromChannel = async (userId) => {
    if (!selectedChannel || !userId) {
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.userSelectionRequired'),
        severity: 'warning'
      })
      return
    }

    try {
      const channelRef = doc(db, 'channels', selectedChannel.id)
      await updateDoc(channelRef, {
        members: arrayRemove(userId),
        memberDetails: selectedChannel.memberDetails.filter((m) => m.userId !== userId)
      })

      setSelectedChannel({
        ...selectedChannel,
        members: selectedChannel.members.filter((id) => id !== userId),
        memberDetails: selectedChannel.memberDetails.filter((m) => m.userId !== userId)
      })
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.removeUserSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Kullanıcı çıkarılırken hata:', error)
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.removeUserError'),
        severity: 'error'
      })
    }
  }

  // Kullanıcı rolünü ve etiketlerini güncelle
  const handleUpdateUserRoleAndTags = async (userId, newRole, newTags) => {
    if (!selectedChannel || !userId) {
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.userSelectionRequired'),
        severity: 'warning'
      })
      return
    }

    try {
      const channelRef = doc(db, 'channels', selectedChannel.id)
      const updatedMemberDetails = selectedChannel.memberDetails.map((member) => {
        if (member.userId === userId) {
          return { ...member, role: newRole, tags: newTags }
        }
        return member
      })

      await updateDoc(channelRef, { memberDetails: updatedMemberDetails })

      setSelectedChannel({
        ...selectedChannel,
        memberDetails: updatedMemberDetails
      })
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.userRoleTagUpdateSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Kullanıcı rolü/etiket güncellenirken hata:', error)
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.userRoleTagUpdateError'),
        severity: 'error'
      })
    }
  }

  // Edit Dialog açma
  const handleOpenEditDialog = (type, item) => {
    if (type === 'channel') {
      setEditDialog({
        open: true,
        type,
        item,
        newName: item.name,
        newShowChannelLink: item.showChannelLink ?? false
      })
    } else if (type === 'workspace') {
      setEditDialog({
        open: true,
        type,
        item,
        newName: item.name,
        newShowChannelLink: false
      })
    } else if (type === 'tag') {
      setEditDialog({
        open: true,
        type,
        item,
        newName: item.tag,
        newShowChannelLink: false
      })
    }
  }

  const handleCloseEditDialog = () => {
    setEditDialog({
      open: false,
      type: '',
      item: null,
      newName: '',
      newShowChannelLink: false
    })
  }

  // Edit işlemi kaydetme
  const handleSaveEdit = async () => {
    const { type, item, newName, newShowChannelLink } = editDialog
    if (!newName.trim()) {
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.editSaveNameEmptyWarning'),
        severity: 'warning'
      })
      return
    }

    try {
      if (type === 'workspace') {
        const workspaceRef = doc(db, 'workspaces', item.id)
        await updateDoc(workspaceRef, { name: newName })
        setSnackbar({
          open: true,
          message: t('adminWorkspaces.editSaveSuccess'),
          severity: 'success'
        })
      } else if (type === 'channel') {
        const channelRef = doc(db, 'channels', item.id)
        await updateDoc(channelRef, {
          name: newName,
          showChannelLink: newShowChannelLink
        })
        setSnackbar({
          open: true,
          message: t('adminWorkspaces.editSaveSuccess'),
          severity: 'success'
        })
      } else if (type === 'tag') {
        const oldTag = item.tag
        const newTagName = newName.trim()

        if (oldTag === newTagName) {
          setSnackbar({
            open: true,
            message: t('adminWorkspaces.tagRenameSame'),
            severity: 'info'
          })
          handleCloseEditDialog()
          return
        }

        // Workspace'teki tag'ı güncelle
        const workspaceRef = doc(db, 'workspaces', selectedWorkspace.id)
        const workspaceSnap = await getDoc(workspaceRef)
        const workspaceData = workspaceSnap.data()

        if (workspaceData.tags.includes(newTagName)) {
          setSnackbar({
            open: true,
            message: t('adminWorkspaces.tagAlreadyExists'),
            severity: 'warning'
          })
          handleCloseEditDialog()
          return
        }

        // Eski etiketi kaldırıp yeni etiketi ekle
        await updateDoc(workspaceRef, { tags: arrayRemove(oldTag) })
        await updateDoc(workspaceRef, { tags: arrayUnion(newTagName) })

        // Tüm kanallarda bu tag'ı kullanan üyeleri güncelle
        const channelsRef = collection(db, 'channels')
        const qChannels = query(channelsRef, where('workspaceId', '==', selectedWorkspace.id))
        const channelsSnap = await getDocs(qChannels)
        const batch = writeBatch(db)

        channelsSnap.forEach((channelDoc) => {
          const channelData = channelDoc.data()
          const updatedMemberDetails = channelData.memberDetails.map((member) => {
            if (member.tags.includes(oldTag)) {
              return {
                ...member,
                tags: member.tags.map((tag) => (tag === oldTag ? newTagName : tag))
              }
            }
            return member
          })
          batch.update(channelDoc.ref, { memberDetails: updatedMemberDetails })
        })

        await batch.commit()

        setSnackbar({
          open: true,
          message: t('adminWorkspaces.editSaveSuccess'),
          severity: 'success'
        })
      }
      handleCloseEditDialog()
    } catch (error) {
      console.error('İsim güncellenirken hata:', error)
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.workspaceCreateError'),
        severity: 'error'
      })
    }
  }

  // Delete Dialog açma
  const handleOpenDeleteDialog = (type, item) => {
    setDeleteDialog({
      open: true,
      type,
      item
    })
  }

  // Delete Dialog kapatma
  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      type: '',
      item: null
    })
  }

  // Silme işlemi
  const handleConfirmDelete = async () => {
    const { type, item } = deleteDialog
    try {
      if (type === 'workspace') {
        const workspaceRef = doc(db, 'workspaces', item.id)
        await deleteDoc(workspaceRef)

        // İlgili kanalları da sil
        const channelsRef = collection(db, 'channels')
        const qChannels = query(channelsRef, where('workspaceId', '==', item.id))
        const channelsSnap = await getDocs(qChannels)
        const batch = writeBatch(db)
        channelsSnap.forEach((docSnapshot) => {
          batch.delete(doc(db, 'channels', docSnapshot.id))
        })
        await batch.commit()

        setSnackbar({
          open: true,
          message: t('adminWorkspaces.deleteSuccess'),
          severity: 'success'
        })
        if (selectedWorkspace?.id === item.id) {
          setSelectedWorkspace(null)
          setChannels([])
          setSelectedChannel(null)
          setTags([])
        }
      } else if (type === 'channel') {
        const channelRef = doc(db, 'channels', item.id)
        await deleteDoc(channelRef)
        setSnackbar({
          open: true,
          message: t('adminWorkspaces.channelDeleteSuccess'),
          severity: 'success'
        })
        if (selectedChannel?.id === item.id) {
          setSelectedChannel(null)
        }
      } else if (type === 'tag') {
        const tag = item.tag
        const workspaceRef = doc(db, 'workspaces', selectedWorkspace.id)
        await updateDoc(workspaceRef, {
          tags: arrayRemove(tag)
        })

        // Kanallardaki üyelerden bu tag'ı kaldır
        const channelsRef = collection(db, 'channels')
        const qChannels = query(channelsRef, where('workspaceId', '==', selectedWorkspace.id))
        const channelsSnap = await getDocs(qChannels)
        const batch = writeBatch(db)

        channelsSnap.forEach((channelDoc) => {
          const channelData = channelDoc.data()
          const updatedMemberDetails = channelData.memberDetails.map((member) => {
            if (member.tags.includes(tag)) {
              return {
                ...member,
                tags: member.tags.filter((t) => t !== tag)
              }
            }
            return member
          })
          batch.update(channelDoc.ref, { memberDetails: updatedMemberDetails })
        })

        await batch.commit()

        setSnackbar({
          open: true,
          message: t('adminWorkspaces.deleteSuccess'),
          severity: 'success'
        })
        setTags((prev) => prev.filter((t) => t !== tag))
      }
      handleCloseDeleteDialog()
    } catch (error) {
      console.error('Silme işlemi sırasında hata:', error)
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.deleteError'),
        severity: 'error'
      })
    }
  }

  // Tag ekleme
  const handleAddTag = async () => {
    if (!newTag.trim()) {
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.tagEmptyWarning'),
        severity: 'warning'
      })
      return
    }

    if (tags.includes(newTag.trim())) {
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.tagExists'),
        severity: 'info'
      })
      return
    }

    try {
      const workspaceRef = doc(db, 'workspaces', selectedWorkspace.id)
      await updateDoc(workspaceRef, { tags: arrayUnion(newTag.trim()) })

      setTags((prev) => [...prev, newTag.trim()])
      setNewTag('')
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.tagAddSuccess'),
        severity: 'success'
      })
    } catch (error) {
      console.error('Etiket eklenirken hata:', error)
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.tagAddError'),
        severity: 'error'
      })
    }
  }

  // Tag silme (tekil) - burada silme diyaloğu ile birlikte handleConfirmDelete() içinde zaten ele alınıyor

  // Kullanıcı ekleme Dialog
  const handleOpenAddMemberDialog = () => {
    setAddMemberDialog({
      open: true,
      user: null
    })
  }
  const handleCloseAddMemberDialog = () => {
    setAddMemberDialog({ open: false, user: null })
  }
  const handleAddMember = async () => {
    if (!addMemberDialog.user) {
      setSnackbar({
        open: true,
        message: t('adminWorkspaces.userSelectionRequired'),
        severity: 'warning'
      })
      return
    }
    await handleAddMemberToChannel(addMemberDialog.user.id)
    handleCloseAddMemberDialog()
  }

  if (!isAdmin) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{t('adminWorkspaces.noAccess')}</Alert>
      </Container>
    )
  }

  return (
    <Container sx={{ mt: 4, backgroundColor: '#f9f9f9', padding: 4, borderRadius: 2 }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ color: '#3f51b5' }}>
        <WorkspacePremiumIcon sx={{ mr: 1 }} />
        {t('adminWorkspaces.pageTitle')}
      </Typography>

      <Grid container spacing={4}>
        {/* 1) Yeni Workspace Oluşturma */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ backgroundColor: '#ffffff', boxShadow: 'none' }}>
            <CardHeader
              title={t('adminWorkspaces.createNewWorkspace')}
              avatar={<AddIcon />}
              sx={{ backgroundColor: '#e3f2fd', borderRadius: 1 }}
            />
            <CardContent>
              <Box display="flex" alignItems="center">
                <TextField
                  label={t('adminWorkspaces.workspaceName')}
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  fullWidth
                  variant="outlined"
                  sx={{ backgroundColor: '#ffffff', borderRadius: 1 }}
                />
                <IconButton color="primary" onClick={handleCreateWorkspace} sx={{ ml: 2 }}>
                  <AddIcon />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 2) Workspace Listesi */}
        <Grid item xs={12} md={12}>
          <Card variant="outlined" sx={{ backgroundColor: '#ffffff', boxShadow: 'none' }}>
            <CardHeader
              title={t('adminWorkspaces.workspaceListTitle')}
              avatar={<WorkspacePremiumIcon />}
              sx={{ backgroundColor: '#e3f2fd', borderRadius: 1 }}
            />
            <Divider />
            <CardContent>
              <List>
                {workspaces.map((ws) => (
                  <ListItem
                    button
                    key={ws.id}
                    selected={selectedWorkspace?.id === ws.id}
                    onClick={() => handleSelectWorkspace(ws)}
                    secondaryAction={
                      <>
                        <IconButton
                          edge="end"
                          aria-label="edit"
                          onClick={() => handleOpenEditDialog('workspace', ws)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleOpenDeleteDialog('workspace', ws)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    }
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor:
                        selectedWorkspace?.id === ws.id ? '#F0C1E1' : '#f1f1f1',
                      '&:hover': {
                        backgroundColor: '#C5D3E8'
                      }
                    }}
                  >
                    <ListItemText
                      primary={ws.name}
                      secondary={ws.description}
                      sx={{ color: '#333' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* 3) Seçili Workspace bilgisi, Tag Yönetimi & Kanal Oluşturma */}
        {selectedWorkspace && (
          <>
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom sx={{ color: '#4caf50' }}>
                <WorkspacePremiumIcon sx={{ mr: 1 }} />
                {t('adminWorkspaces.selectedWorkspace')}: {selectedWorkspace.name}
              </Typography>
            </Grid>

            {/* Tag Yönetimi */}
            <Grid item xs={12} md={12}>
              <Card variant="outlined" sx={{ backgroundColor: '#ffffff', boxShadow: 'none' }}>
                <CardHeader
                  title={t('adminWorkspaces.tagManagement')}
                  avatar={<TagIcon />}
                  sx={{ backgroundColor: '#e3f2fd', borderRadius: 1 }}
                />
                <Divider />
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <TextField
                      label={t('adminWorkspaces.newTag')}
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      fullWidth
                      variant="outlined"
                      sx={{ backgroundColor: '#ffffff', borderRadius: 1 }}
                    />
                    <IconButton color="primary" onClick={handleAddTag} sx={{ ml: 2 }}>
                      <AddIcon />
                    </IconButton>
                  </Box>
                  <Grid container spacing={1}>
                    {tags.map((tag, index) => (
                      <Grid item xs={12} sm={tags.length === 1 ? 12 : 6} key={index}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ backgroundColor: '#f1f1f1', borderRadius: 1, padding: 1 }}
                        >
                          <Chip label={tag} color="secondary" />
                          <Box>
                            <IconButton
                              aria-label="edit"
                              onClick={() => handleOpenEditDialog('tag', { tag })}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleOpenDeleteDialog('tag', { tag })}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                    {tags.length === 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                          {t('adminWorkspaces.noTagsYet')}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Kanal Oluşturma */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ backgroundColor: '#ffffff', boxShadow: 'none' }}>
                <CardHeader
                  title={t('adminWorkspaces.createNewChannel')}
                  avatar={<AddIcon />}
                  sx={{ backgroundColor: '#e3f2fd', borderRadius: 1 }}
                />
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <TextField
                      label={t('adminWorkspaces.channelName')}
                      value={channelName}
                      onChange={(e) => setChannelName(e.target.value)}
                      fullWidth
                      variant="outlined"
                      sx={{ backgroundColor: '#ffffff', borderRadius: 1 }}
                    />
                    <FormControlLabel
                      sx={{
                        ml: 2,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          color: '#444'
                        }
                      }}
                      control={
                        <Checkbox
                          checked={showChannelLink}
                          onChange={(e) => setShowChannelLink(e.target.checked)}
                          icon={<RadioButtonUncheckedIcon />}
                          checkedIcon={<CheckCircleIcon />}
                          sx={{
                            color: '#9e9e9e',
                            '&.Mui-checked': {
                              color: '#4caf50'
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(76, 175, 80, 0.04)'
                            },
                            transition: '0.2s'
                          }}
                        />
                      }
                      label={t('adminWorkspaces.channelCheckboxLabel')}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleCreateChannel}
                      sx={{ ml: 2 }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Kanal Listesi */}
            <Grid item xs={12} md={12}>
              <Card variant="outlined" sx={{ backgroundColor: '#ffffff', boxShadow: 'none' }}>
                <CardHeader
                  title={t('adminWorkspaces.channelsTitle')}
                  avatar={<ChannelIcon />}
                  sx={{ backgroundColor: '#e3f2fd', borderRadius: 1 }}
                />
                <Divider />
                <CardContent>
                  <List>
                    {channels.map((channel) => (
                      <ListItem
                        button
                        key={channel.id}
                        selected={selectedChannel?.id === channel.id}
                        onClick={() => handleSelectChannel(channel)}
                        sx={{
                          borderRadius: 1,
                          mb: 1,
                          backgroundColor:
                            selectedChannel?.id === channel.id ? '#F0C1E1' : '#f1f1f1',
                          '&:hover': {
                            backgroundColor: '#C5D3E8'
                          }
                        }}
                      >
                        <ListItemText
                          primary={channel.name}
                          secondary={`${t('adminWorkspaces.memberCount')}: ${
                            channel.members?.length || 0
                          }`}
                          sx={{ color: '#333' }}
                        />
                        {/* KANAL LİSTESİNDE GÖSTERİLEN CHECKBOX (Sadece gösterim) */}
                        <Box sx={{ mr: 2 }}>
                          <Checkbox checked={channel.showChannelLink || false} disabled />
                        </Box>
                        <IconButton
                          edge="end"
                          aria-label="edit"
                          onClick={() => handleOpenEditDialog('channel', channel)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleOpenDeleteDialog('channel', channel)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* 4) Seçili Kanal & Üye Ekleme */}
        {selectedChannel && (
          <>
            {/* Kullanıcı Ekleme Kartı */}
            <Grid item xs={12} md={12}>
              <Card variant="outlined" sx={{ backgroundColor: '#ffffff', boxShadow: 'none' }}>
                <CardHeader
                  title={t('adminWorkspaces.addNewMember')}
                  avatar={<PersonAddIcon />}
                  sx={{ backgroundColor: '#e3f2fd', borderRadius: 1 }}
                />
                <Divider />
                <CardContent>
                  <Box display="flex" flexDirection="column">
                    <TextField
                      label={t('adminWorkspaces.searchUser')}
                      value={searchUser}
                      onChange={(e) => setSearchUser(e.target.value)}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        )
                      }}
                      sx={{ mb: 2, backgroundColor: '#ffffff', borderRadius: 1 }}
                    />
                    <List
                      sx={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        border: '0px solid #e0e0e0',
                        borderRadius: '4px',
                        '&::-webkit-scrollbar': {
                          width: '8px'
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: '#f1f1f1'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#c1c1c1',
                          borderRadius: '4px'
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          backgroundColor: '#a8a8a8'
                        }
                      }}
                    >
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                          <ListItem
                            button
                            key={user.id}
                            onClick={() => {
                              setAddMemberDialog({ open: true, user })
                            }}
                            sx={{
                              borderRadius: 1,
                              mb: 1,
                              backgroundColor: '#f1f8e9',
                              '&:hover': { backgroundColor: '#dcedc8' }
                            }}
                          >
                            <ListItemAvatar>
                              {user.avatarUrl ? (
                                <Avatar alt={user.nickname} src={user.avatarUrl} />
                              ) : (
                                <Avatar>
                                  {user.nickname
                                    ? user.nickname.charAt(0).toUpperCase()
                                    : user.email.charAt(0).toUpperCase()}
                                </Avatar>
                              )}
                            </ListItemAvatar>
                            <ListItemText
                              primary={user.nickname || user.email}
                              secondary={`${user.firstName || ''} ${
                                user.lastName || ''
                              }`}
                              sx={{ ml: 2 }}
                            />
                            <IconButton edge="end" color="primary">
                              <PersonAddIcon />
                            </IconButton>
                          </ListItem>
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          {t('adminWorkspaces.userNotFound')}
                        </Typography>
                      )}
                    </List>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Kanalın Üye Listesi & Roller */}
            <Grid item xs={12} md={12}>
              <Card variant="outlined" sx={{ backgroundColor: '#ffffff', boxShadow: 'none' }}>
                <CardHeader
                  title={`${selectedChannel.name} - ${t('adminWorkspaces.membersTitle')}`}
                  avatar={<PersonAddIcon />}
                  sx={{ backgroundColor: '#e3f2fd', borderRadius: 1 }}
                />
                <Divider />
                <CardContent>
                  <List
                    sx={{
                      maxHeight: '400px',
                      overflowY: 'auto',
                      border: '0px solid #e0e0e0',
                      borderRadius: '4px',
                      '&::-webkit-scrollbar': {
                        width: '8px'
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '4px'
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: '#a8a8a8'
                      }
                    }}
                  >
                    {selectedChannel.memberDetails?.map((member) => {
                      const user = users.find((u) => u.id === member.userId)
                      return (
                        <ListItem
                          key={member.userId}
                          sx={{
                            borderRadius: 1,
                            mb: 1,
                            backgroundColor: '#f7eed7',
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            flexWrap: 'wrap',
                            p: 1
                          }}
                        >
                          {/* Kullanıcı Avatar ve Bilgiler */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: { xs: '100%', sm: 'auto' },
                              mr: { xs: 0, sm: 2 },
                              mb: { xs: 1, sm: 0 }
                            }}
                          >
                            <ListItemAvatar>
                              {user && user.avatarUrl ? (
                                <Avatar alt={user.nickname} src={user.avatarUrl} />
                              ) : (
                                <Avatar>
                                  {user && user.nickname
                                    ? user.nickname.charAt(0).toUpperCase()
                                    : 'U'}
                                </Avatar>
                              )}
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                user ? user.nickname || user.email : member.userId
                              }
                              secondary={`${user ? user.firstName : ''} ${
                                user ? user.lastName : ''
                              }`}
                              sx={{ ml: 2 }}
                            />
                          </Box>

                          {/* Rol + Tag + Silme Butonu */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'flex-start', sm: 'center' },
                              width: { xs: '100%', sm: 'auto' },
                              gap: 2
                            }}
                          >
                            {/* Rol Seçimi */}
                            <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                              <InputLabel id={`role-select-label-${member.userId}`}>
                                {t('adminWorkspaces.roleSelectLabel')}
                              </InputLabel>
                              <Select
                                labelId={`role-select-label-${member.userId}`}
                                value={member.role || 'participant'}
                                onChange={(e) =>
                                  handleUpdateUserRoleAndTags(
                                    member.userId,
                                    e.target.value,
                                    member.tags
                                  )
                                }
                                label={t('adminWorkspaces.roleSelectLabel')}
                              >
                                <MenuItem value="moderator">Moderator</MenuItem>
                                <MenuItem value="observer">Observer</MenuItem>
                                <MenuItem value="participant">Participant</MenuItem>
                              </Select>
                            </FormControl>

                            {/* Etiket Seçimi (Autocomplete) */}
                            <Box sx={{ minWidth: 200 }}>
                              <Autocomplete
                                multiple
                                options={tags}
                                getOptionLabel={(option) => option}
                                value={member.tags}
                                onChange={(event, newValue) => {
                                  handleUpdateUserRoleAndTags(
                                    member.userId,
                                    member.role,
                                    newValue
                                  )
                                }}
                                renderTags={(value, getTagProps) =>
                                  value.map((option, index) => (
                                    <Chip
                                      variant="outlined"
                                      label={option}
                                      {...getTagProps({ index })}
                                      key={index}
                                    />
                                  ))
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label={t('adminWorkspaces.tagsLabel')}
                                    placeholder={t('adminWorkspaces.tagsLabel')}
                                  />
                                )}
                              />
                            </Box>

                            {/* Üye Silme Butonu */}
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => handleRemoveMemberFromChannel(member.userId)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </ListItem>
                      )
                    })}
                    {(!selectedChannel.memberDetails ||
                      selectedChannel.memberDetails.length === 0) && (
                      <Typography variant="body2" color="textSecondary">
                        {t('adminWorkspaces.noMembers')}
                      </Typography>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Kullanıcı Ekleme Dialog */}
      <Dialog open={addMemberDialog.open} onClose={handleCloseAddMemberDialog}>
        <DialogTitle>{t('adminWorkspaces.addMemberDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            {addMemberDialog.user
              ? `${t('adminWorkspaces.searchUser')}: ${addMemberDialog.user.email}`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddMemberDialog} color="secondary">
            {t('adminWorkspaces.cancel')}
          </Button>
          <Button onClick={handleAddMember} color="primary" disabled={!addMemberDialog.user}>
            {t('adminWorkspaces.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={handleCloseEditDialog}>
        <DialogTitle>
          {editDialog.type === 'workspace'
            ? t('adminWorkspaces.workspaceEditDialogTitle')
            : editDialog.type === 'channel'
            ? t('adminWorkspaces.channelEditDialogTitle')
            : t('adminWorkspaces.tagEditDialogTitle')}
        </DialogTitle>
        <DialogContent>
          {editDialog.type === 'tag' ? (
            <TextField
              autoFocus
              margin="dense"
              label={t('adminWorkspaces.editDialogNewTag')}
              type="text"
              fullWidth
              variant="outlined"
              value={editDialog.newName}
              onChange={(e) =>
                setEditDialog({ ...editDialog, newName: e.target.value })
              }
            />
          ) : (
            <>
              <TextField
                autoFocus
                margin="dense"
                label={t('adminWorkspaces.editDialogNewName')}
                type="text"
                fullWidth
                variant="outlined"
                value={editDialog.newName}
                onChange={(e) =>
                  setEditDialog({ ...editDialog, newName: e.target.value })
                }
              />
              {/* Sadece kanal düzenlenirken */}
              {editDialog.type === 'channel' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editDialog.newShowChannelLink}
                      onChange={(e) =>
                        setEditDialog({ ...editDialog, newShowChannelLink: e.target.checked })
                      }
                      color="primary"
                    />
                  }
                  label={t('adminWorkspaces.channelCheckboxLabel')}
                  sx={{ mt: 1 }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} color="secondary">
            {t('adminWorkspaces.cancel')}
          </Button>
          <Button onClick={handleSaveEdit} color="primary">
            {t('adminWorkspaces.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{t('adminWorkspaces.deleteDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteDialog.type === 'workspace'
              ? t('adminWorkspaces.deleteDialogWorkspaceConfirm', {
                  name: deleteDialog.item?.name
                })
              : deleteDialog.type === 'channel'
              ? t('adminWorkspaces.deleteDialogChannelConfirm')
              : t('adminWorkspaces.deleteDialogTagConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="secondary">
            {t('adminWorkspaces.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="primary">
            {t('adminWorkspaces.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}